import React, { useEffect, useState } from "react";
import { FiCheckCircle, FiTool } from "react-icons/fi";
import { Modal } from "../Modal";
import { LoadingSpinner } from "../LoadingSpinner";
import GatedButton from "../GatedButton";
import ContentPickerPanel, { PickedContent } from "./ContentPickerPanel";
import {
	FixSourceResult,
	displayPath,
	extractErrorMessage,
	fixContentSource,
	fixTaskSource
} from "./redirectsApi";

/** Cible de la correction : une tâche existante, ou un contenu avant retrait (aucune tâche encore). */
export type FixSourceTarget = { taskId: string } | { oldContentId: string };

type FixSourceModalProps = {
	show: boolean;
	onHide: () => void;
	/** Tâche ou contenu à corriger : null tant que rien n'est ouvert. */
	target: FixSourceTarget | null;
	/** L'URL cassée, affichée en tête pour situer l'éditeur. */
	contextUrl?: string | null;
	/** Contenu retiré : masqué du sélecteur (il ne peut pas être son propre remplacement). */
	excludeContentId?: string | null;
	/** Appelé après une correction réussie : le parent rafraîchit ses listes/compteurs. */
	onFixed?: (result: FixSourceResult) => void;
};

/**
 * Modale « Corriger à la source » : l'éditeur choisit la page de remplacement dans un sélecteur
 * de tous les contenus (filtre par type + recherche), puis le backend réécrit chaque lien statique
 * et repointe les redirections qui visaient l'URL retirée. Le panneau de résultat récapitule les
 * réécritures et liste les redirections refusées (anti-boucle) avec leur message.
 */
const FixSourceModal: React.FC<FixSourceModalProps> = ({
	show,
	onHide,
	target,
	contextUrl,
	excludeContentId,
	onFixed
}) => {
	const [selected, setSelected] = useState<PickedContent | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [result, setResult] = useState<FixSourceResult | null>(null);

	// Chaque ouverture repart d'un état vierge (sélection, erreur, résultat).
	useEffect(() => {
		if (show) {
			setSelected(null);
			setSubmitting(false);
			setError(null);
			setResult(null);
		}
	}, [show, target]);

	const submit = async () => {
		if (!target || !selected?.id) return;
		setSubmitting(true);
		setError(null);
		try {
			const fixResult =
				"taskId" in target
					? await fixTaskSource(target.taskId, selected.id)
					: await fixContentSource(target.oldContentId, selected.id);
			setResult(fixResult);
			onFixed?.(fixResult);
		} catch (err) {
			console.error("Échec de la correction à la source", err);
			setError(extractErrorMessage(err) ?? "La correction a échoué. Réessayez.");
		} finally {
			setSubmitting(false);
		}
	};

	const close = () => {
		if (!submitting) onHide();
	};

	return (
		<Modal show={show} onHide={close} size="md" centered>
			<Modal.Header closeButton>
				<Modal.Title>Corriger les liens à la source</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{result ? (
					<div className="space-y-3">
						<div
							className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${
								result.taskResolved || (result.remainingStaticLinks === 0 && result.remainingTargetingRedirects === 0)
									? "border-emerald-200 bg-emerald-50"
									: "border-amber-200 bg-amber-50"
							}`}
						>
							<FiCheckCircle
								className={`mt-0.5 h-5 w-5 shrink-0 ${
									result.taskResolved || (result.remainingStaticLinks === 0 && result.remainingTargetingRedirects === 0)
										? "text-emerald-600"
										: "text-amber-600"
								}`}
							/>
							<div className="text-sm text-slate-700">
								<p className="font-semibold text-slate-900">
									{result.rewrittenContentIds.length} lien{result.rewrittenContentIds.length > 1 ? "s" : ""} réécrit
									{result.rewrittenContentIds.length > 1 ? "s" : ""}, {result.repointedRedirects.length} redirection
									{result.repointedRedirects.length > 1 ? "s" : ""} repointée{result.repointedRedirects.length > 1 ? "s" : ""}.
								</p>
								{result.taskResolved ? (
									<p className="mt-0.5">La tâche est résolue : plus rien ne dépend de l'URL retirée.</p>
								) : (
									<p className="mt-0.5">
										Il reste {result.remainingStaticLinks} lien{result.remainingStaticLinks > 1 ? "s" : ""} statique
										{result.remainingStaticLinks > 1 ? "s" : ""} et {result.remainingTargetingRedirects} redirection
										{result.remainingTargetingRedirects > 1 ? "s" : ""} à traiter.
									</p>
								)}
							</div>
						</div>
						{result.refusedRedirects.length > 0 && (
							<div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
								<p className="text-sm font-semibold text-rose-800">
									Redirection{result.refusedRedirects.length > 1 ? "s" : ""} refusée
									{result.refusedRedirects.length > 1 ? "s" : ""} :
								</p>
								<ul className="mt-1 space-y-1 text-sm text-rose-700">
									{result.refusedRedirects.map((refused, index) => (
										<li key={`refused-${index}`}>
											<span className="font-mono text-xs">{displayPath(refused.fromPath)}</span>
											{refused.message ? ` : ${refused.message}` : ""}
										</li>
									))}
								</ul>
							</div>
						)}
					</div>
				) : (
					<div className="space-y-3">
						<p className="text-sm text-slate-600">
							Choisissez la page qui remplace{" "}
							{contextUrl ? (
								<span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-700" title={displayPath(contextUrl)}>
									{displayPath(contextUrl)}
								</span>
							) : (
								"l'URL retirée"
							)}
							{" : tous les liens statiques seront réécrits et les redirections repointées vers elle."}
						</p>
						<ContentPickerPanel
							excludeIds={[excludeContentId]}
							selectedId={selected?.id ?? null}
							onSelect={setSelected}
						/>
						{selected && (
							<p className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
								Remplacement choisi : <span className="font-semibold">{selected.title}</span>
							</p>
						)}
						{error && (
							<p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">
								{error}
							</p>
						)}
					</div>
				)}
			</Modal.Body>
			<Modal.Footer>
				{result ? (
					<button
						type="button"
						onClick={close}
						className="inline-flex items-center rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
					>
						Fermer
					</button>
				) : (
					<>
						<button
							type="button"
							onClick={close}
							disabled={submitting}
							className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:opacity-60"
						>
							Annuler
						</button>
						<GatedButton
							token="feature:redirections:write"
							onClick={submit}
							disabled={!selected || submitting}
							className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
						>
							{submitting ? <LoadingSpinner size="sm" inline /> : <FiTool className="h-4 w-4" />}
							{submitting ? "Correction…" : "Réécrire les liens"}
						</GatedButton>
					</>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default FixSourceModal;
